import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { queryKeys, useApiMutation, useApiQuery } from '../api/hooks';
import type {
  ChangePackageStatusRequest,
  CreatePackageRequest,
  CreatePromotionRequest,
  DiscountType,
  PackageKind,
  PackageResponse,
  PackageSummaryResponse,
  ProductStatus,
  PromotionResponse,
  ServiceResponse,
  VatClass,
} from '../api/types';
import { AsyncSection } from '../components/AsyncSection';
import { useChartsShown } from '../components/charts/useChartsShown';
import { PageHeader } from '../components/PageHeader';
import { ErrorState, LoadingState } from '../components/states/StateViews';
import { useAuthStore } from '../auth/authStore';
import { BilingualNameFields, Field, MoneyField } from './catalog/fields';
import { money, readMoney } from './catalog/money';
import { useLocalisedName } from '../i18n/localisedName';
import { PackagesOverview } from './insight/PackagesOverview';
import { clinicToday, shiftDay } from './scheduling/clinicTime';

/**
 * Packages and promotions — what a clinic sells beyond a single session (FR-203, FR-204, FR-205).
 *
 * They share a screen because they are the same job: building the offer. A clinic putting together
 * a summer campaign defines the bundle and the discount in one sitting, and splitting them across
 * two destinations would only make that two visits.
 *
 * Above them, for whoever may read the takings, packages in numbers (CR-36): what sold, what
 * patients still hold and what it is worth, where the sessions go, and what is about to expire.
 */
export function PackagesPage() {
  const { t } = useTranslation();
  const clinicId = useAuthStore((state) => state.activeClinicId);
  const permissions = useAuthStore((state) => state.permissions);
  const canSeeMoney = permissions.has('CASH_SUMMARY_READ');
  const [chartsShown, showCharts] = useChartsShown('packages');

  if (!clinicId) {
    return <PageHeader title={t('nav.items.packages')} description={t('home.noClinic')} />;
  }

  return (
    <>
      <PageHeader
        title={t('nav.items.packages')}
        description={t('packages.description')}
        actions={
          canSeeMoney ? (
            <button type="button" className="button button--ghost" onClick={() => showCharts(!chartsShown)}>
              {chartsShown ? t('overview.hide') : t('overview.show')}
            </button>
          ) : null
        }
      />
      {canSeeMoney && chartsShown ? <PackagesCharts clinicId={clinicId} /> : null}
      <PackagesSection clinicId={clinicId} canManage={permissions.has('PACKAGE_MANAGE')} />
      <PromotionsSection clinicId={clinicId} canManage={permissions.has('PROMOTION_MANAGE')} />
    </>
  );
}

/** The periods the charts offer, in days, each ending today (CR-36). */
const PERIODS = [30, 90, 365] as const;

/**
 * The charts and their period (CR-36). What is held and what is expiring are as of today whatever
 * the period; the period decides what counts as sold and used.
 */
function PackagesCharts({ clinicId }: { readonly clinicId: string }) {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<(typeof PERIODS)[number]>(90);
  const to = clinicToday();
  const from = shiftDay(to, -(period - 1));
  const search = `from=${from}&to=${to}`;
  const summary = useApiQuery<PackageSummaryResponse>(
    queryKeys.packageSummary(clinicId, search),
    `/clinics/${clinicId}/packages/summary?${search}`,
    { keepPrevious: true },
  );

  return (
    <>
      <div className="toolbar overview-bar">
        <div className="tabs" role="tablist" aria-label={t('overview.sales.period')}>
          {PERIODS.map((days) => (
            <button
              key={days}
              type="button"
              role="tab"
              aria-selected={period === days}
              className={period === days ? 'tab tab--active' : 'tab'}
              onClick={() => setPeriod(days)}
            >
              {t(`overview.sales.periods.${days}`)}
            </button>
          ))}
        </div>
      </div>
      {summary.data ? (
        <PackagesOverview summary={summary.data} from={from} to={to} />
      ) : summary.isPending ? (
        <LoadingState rows={3} />
      ) : summary.error ? (
        <ErrorState error={summary.error} onRetry={() => void summary.refetch()} />
      ) : null}
    </>
  );
}

// ── Packages ────────────────────────────────────────────────────────────────

function PackagesSection({ clinicId, canManage }: { readonly clinicId: string; readonly canManage: boolean }) {
  const localised = useLocalisedName();
  const { t } = useTranslation();
  const [creating, setCreating] = useState(false);

  const packages = useApiQuery<PackageResponse[]>(queryKeys.packages(clinicId), `/clinics/${clinicId}/packages`);
  const services = useApiQuery<ServiceResponse[]>(
    queryKeys.services(clinicId),
    `/clinics/${clinicId}/catalog/services`,
  );

  const createPackage = useApiMutation<PackageResponse, CreatePackageRequest>(
    (body) => ({ path: `/clinics/${clinicId}/packages`, options: { method: 'POST', body } }),
    { invalidates: [queryKeys.packages(clinicId)], onSuccess: () => setCreating(false) },
  );

  const changeStatus = useApiMutation<PackageResponse, { readonly id: string; readonly status: ProductStatus }>(
    ({ id, status }) => ({
      path: `/clinics/${clinicId}/packages/${id}/status`,
      options: { method: 'PUT', body: { status } satisfies ChangePackageStatusRequest },
    }),
    { invalidates: [queryKeys.packages(clinicId)] },
  );

  return (
    <section className="section" aria-labelledby="packages-heading">
      <h2 className="section__title" id="packages-heading">
        {t('packages.title')}
      </h2>
      <p className="field__hint" style={{ marginBlockEnd: 'var(--space-3)' }}>
        {t('packages.hint')}
      </p>

      {canManage ? (
        <button
          type="button"
          className="button button--ghost"
          style={{ marginBlockEnd: 'var(--space-3)' }}
          onClick={() => setCreating((open) => !open)}
        >
          {creating ? t('common.cancel') : t('packages.add')}
        </button>
      ) : null}

      {creating ? (
        <PackageForm
          services={services.data ?? []}
          onSubmit={(body) => createPackage.mutate(body)}
          pending={createPackage.isPending}
          error={createPackage.error}
        />
      ) : null}

      <AsyncSection
        query={packages}
        isEmpty={(rows) => rows.length === 0}
        emptyTitle={t('packages.emptyTitle')}
        emptyDescription={t('packages.emptyDescription')}
      >
        {(rows) => (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('catalog.code')}</th>
                  <th scope="col">{t('catalog.name')}</th>
                  <th scope="col">{t('packages.kind')}</th>
                  <th scope="col">{t('packages.contents')}</th>
                  <th scope="col">{t('catalog.price')}</th>
                  <th scope="col">{t('packages.expiry')}</th>
                  <th scope="col">{t('common.status')}</th>
                  {canManage ? <th scope="col">{t('common.actions')}</th> : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((product) => (
                  <tr key={product.id}>
                    <th scope="row" className="ltr">
                      {product.code}
                    </th>
                    <td>{localised.name(product)}</td>
                    <td>{t(`packages.kinds.${product.kind ?? 'SESSION'}`)}</td>
                    <td className="numeric">
                      {product.kind === 'PULSE'
                        ? t('packages.pulses', { n: product.pulseAmount ?? 0 })
                        : t('packages.sessions', { n: product.sessionCount ?? 0 })}
                    </td>
                    <td className="numeric">{money(product.price) ?? t('catalog.unpriced')}</td>
                    <td className="numeric">{t('packages.months', { n: product.expiryMonths ?? 12 })}</td>
                    <td>
                      <span className={product.status === 'ACTIVE' ? 'badge' : 'badge badge--muted'}>
                        {t(`packages.statuses.${product.status ?? 'DRAFT'}`)}
                      </span>
                    </td>
                    {canManage ? (
                      <td>
                        <button
                          type="button"
                          className="button button--ghost"
                          disabled={changeStatus.isPending || !product.price}
                          onClick={() =>
                            changeStatus.mutate({
                              id: product.id ?? '',
                              status: product.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE',
                            })
                          }
                        >
                          {product.status === 'ACTIVE' ? t('packages.pause') : t('packages.putOnSale')}
                        </button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AsyncSection>

      {changeStatus.error ? <ErrorState error={changeStatus.error} /> : null}
    </section>
  );
}

function PackageForm({
  services,
  onSubmit,
  pending,
  error,
}: {
  readonly services: readonly ServiceResponse[];
  readonly onSubmit: (body: CreatePackageRequest) => void;
  readonly pending: boolean;
  readonly error: unknown;
}) {
  const localised = useLocalisedName();
  const { t } = useTranslation();
  const [kind, setKind] = useState<PackageKind>('SESSION');

  return (
    <form
      className="panel"
      style={{ marginBlockEnd: 'var(--space-4)' }}
      onSubmit={(event) => {
        event.preventDefault();
        const form = new FormData(event.currentTarget);
        const quantity = Number(form.get('quantity') ?? 1);
        onSubmit({
          code: String(form.get('code') ?? '').trim(),
          kind,
          nameAr: String(form.get('nameAr') ?? '').trim() || undefined,
          nameEn: String(form.get('nameEn') ?? '').trim() || undefined,
          vatClass: String(form.get('vatClass') ?? 'COSMETIC') as VatClass,
          price: readMoney(form.get('price')),
          sessionCount: kind === 'SESSION' ? quantity : undefined,
          pulseAmount: kind === 'PULSE' ? quantity : undefined,
          expiryMonths: Number(form.get('expiryMonths') ?? 12),
          displayOrder: 0,
          serviceIds: form.getAll('serviceIds').map(String),
        });
      }}
    >
      <div className="form-grid">
        <Field name="code" label={t('catalog.code')} required dir="ltr" />
        <BilingualNameFields />
        <label className="field">
          <span className="field__label">{t('packages.kind')}</span>
          <select
            name="kind"
            className="field__input"
            value={kind}
            onChange={(event) => setKind(event.target.value as PackageKind)}
          >
            <option value="SESSION">{t('packages.kinds.SESSION')}</option>
            <option value="PULSE">{t('packages.kinds.PULSE')}</option>
          </select>
        </label>
        <label className="field">
          <span className="field__label">
            {kind === 'PULSE' ? t('packages.pulseAmount') : t('packages.sessionCount')}
          </span>
          <input
            type="number"
            name="quantity"
            className="field__input numeric"
            min={1}
            defaultValue={kind === 'PULSE' ? 12000 : 6}
            required
          />
        </label>
        <MoneyField name="price" label={t('catalog.price')} />
        <label className="field">
          <span className="field__label">{t('packages.expiry')}</span>
          <input type="number" name="expiryMonths" className="field__input numeric" min={1} defaultValue={12} />
          <span className="field__hint">{t('packages.expiryHint')}</span>
        </label>
        <label className="field">
          <span className="field__label">{t('catalog.vatClass')}</span>
          <select name="vatClass" className="field__input" defaultValue="COSMETIC">
            <option value="COSMETIC">{t('catalog.vatClasses.COSMETIC')}</option>
            <option value="THERAPEUTIC">{t('catalog.vatClasses.THERAPEUTIC')}</option>
          </select>
        </label>
      </div>

      <fieldset style={{ marginBlockStart: 'var(--space-4)', border: 0, padding: 0 }}>
        <legend className="field__label">{t('packages.services')}</legend>
        <p className="field__hint">{t('packages.servicesHint')}</p>
        {services.map((service) => (
          <label className="field" key={service.id}>
            <span className="field__label">
              <input type="checkbox" name="serviceIds" value={service.id ?? ''} />{' '}
              {localised.name(service, service.code)}
            </span>
          </label>
        ))}
      </fieldset>

      {error ? <ErrorState error={error} /> : null}

      <div style={{ marginBlockStart: 'var(--space-4)' }}>
        <button type="submit" className="button button--primary" disabled={pending}>
          {pending ? t('catalog.saving') : t('packages.create')}
        </button>
      </div>
    </form>
  );
}

// ── Promotions ──────────────────────────────────────────────────────────────

function PromotionsSection({ clinicId, canManage }: { readonly clinicId: string; readonly canManage: boolean }) {
  const localised = useLocalisedName();
  const { t } = useTranslation();
  const [creating, setCreating] = useState(false);

  const promotions = useApiQuery<PromotionResponse[]>(
    queryKeys.promotions(clinicId),
    `/clinics/${clinicId}/promotions`,
  );

  const createPromotion = useApiMutation<PromotionResponse, CreatePromotionRequest>(
    (body) => ({ path: `/clinics/${clinicId}/promotions`, options: { method: 'POST', body } }),
    { invalidates: [queryKeys.promotions(clinicId)], onSuccess: () => setCreating(false) },
  );

  return (
    <section className="section" aria-labelledby="promotions-heading">
      <h2 className="section__title" id="promotions-heading">
        {t('promotions.title')}
      </h2>
      <p className="field__hint" style={{ marginBlockEnd: 'var(--space-3)' }}>
        {t('promotions.hint')}
      </p>

      {canManage ? (
        <button
          type="button"
          className="button button--ghost"
          style={{ marginBlockEnd: 'var(--space-3)' }}
          onClick={() => setCreating((open) => !open)}
        >
          {creating ? t('common.cancel') : t('promotions.add')}
        </button>
      ) : null}

      {creating ? (
        <PromotionForm
          onSubmit={(body) => createPromotion.mutate(body)}
          pending={createPromotion.isPending}
          error={createPromotion.error}
        />
      ) : null}

      <AsyncSection
        query={promotions}
        isEmpty={(rows) => rows.length === 0}
        emptyTitle={t('promotions.emptyTitle')}
        emptyDescription={t('promotions.emptyDescription')}
      >
        {(rows) => (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('catalog.code')}</th>
                  <th scope="col">{t('catalog.name')}</th>
                  <th scope="col">{t('promotions.discount')}</th>
                  <th scope="col">{t('promotions.from')}</th>
                  <th scope="col">{t('promotions.until')}</th>
                  <th scope="col">{t('common.status')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((promotion) => (
                  <tr key={promotion.id}>
                    <th scope="row" className="ltr">
                      {promotion.code}
                    </th>
                    <td>{localised.name(promotion)}</td>
                    <td className="numeric">
                      {promotion.discountType === 'PERCENTAGE'
                        ? `${(promotion.percentageBasisPoints ?? 0) / 100}%`
                        : (money(promotion.fixedAmount) ?? '—')}
                    </td>
                    <td className="numeric">{promotion.validFrom}</td>
                    <td className="numeric">{promotion.validUntil}</td>
                    <td>
                      <span className={promotion.state === 'LIVE' ? 'badge' : 'badge badge--muted'}>
                        {t(`promotions.states.${promotion.state ?? 'SCHEDULED'}`)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AsyncSection>
    </section>
  );
}

function PromotionForm({
  onSubmit,
  pending,
  error,
}: {
  readonly onSubmit: (body: CreatePromotionRequest) => void;
  readonly pending: boolean;
  readonly error: unknown;
}) {
  const { t } = useTranslation();
  const [discountType, setDiscountType] = useState<DiscountType>('PERCENTAGE');

  return (
    <form
      className="panel"
      style={{ marginBlockEnd: 'var(--space-4)' }}
      onSubmit={(event) => {
        event.preventDefault();
        const form = new FormData(event.currentTarget);
        const percentage = Number(form.get('percentage') ?? 0);
        onSubmit({
          code: String(form.get('code') ?? '').trim(),
          nameAr: String(form.get('nameAr') ?? '').trim() || undefined,
          nameEn: String(form.get('nameEn') ?? '').trim() || undefined,
          discountType,
          // The API counts in basis points so that 12.5% is a value rather than a rounding argument.
          percentageBasisPoints: discountType === 'PERCENTAGE' ? Math.round(percentage * 100) : undefined,
          fixedAmount: discountType === 'FIXED_AMOUNT' ? readMoney(form.get('fixedAmount')) : undefined,
          scope: 'EVERYTHING',
          validFrom: String(form.get('validFrom') ?? ''),
          validUntil: String(form.get('validUntil') ?? ''),
          offeredAtAllBranches: true,
        });
      }}
    >
      <div className="form-grid">
        <Field name="code" label={t('catalog.code')} required dir="ltr" />
        <BilingualNameFields />
        <label className="field">
          <span className="field__label">{t('promotions.discountType')}</span>
          <select
            name="discountType"
            className="field__input"
            value={discountType}
            onChange={(event) => setDiscountType(event.target.value as DiscountType)}
          >
            <option value="PERCENTAGE">{t('promotions.types.PERCENTAGE')}</option>
            <option value="FIXED_AMOUNT">{t('promotions.types.FIXED_AMOUNT')}</option>
          </select>
        </label>
        {discountType === 'PERCENTAGE' ? (
          <label className="field">
            <span className="field__label">{t('promotions.percentage')}</span>
            <input
              type="number"
              name="percentage"
              className="field__input numeric"
              min={0.01}
              max={100}
              step="0.01"
              defaultValue={20}
              required
            />
          </label>
        ) : (
          <MoneyField name="fixedAmount" label={t('promotions.amount')} />
        )}
        <label className="field">
          <span className="field__label">{t('promotions.from')}</span>
          <input type="date" name="validFrom" className="field__input numeric" required />
        </label>
        <label className="field">
          <span className="field__label">{t('promotions.until')}</span>
          <input type="date" name="validUntil" className="field__input numeric" required />
          <span className="field__hint">{t('promotions.untilHint')}</span>
        </label>
      </div>

      {error ? <ErrorState error={error} /> : null}

      <div style={{ marginBlockStart: 'var(--space-4)' }}>
        <button type="submit" className="button button--primary" disabled={pending}>
          {pending ? t('catalog.saving') : t('promotions.create')}
        </button>
      </div>
    </form>
  );
}
